import { format } from "date-fns";

import {
  useStakeholderDetail,
  useUpdateStakeholder,
} from "@/application/use-cases/stakeholders/use-stakeholders.queries";
import { Input } from "@monorepo-apps/ui/src/components/input";
import { Label } from "@monorepo-apps/ui/src/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@monorepo-apps/ui/src/components/select";
import { Textarea } from "@monorepo-apps/ui/src/components/textarea";
import type { Area, Org } from "@/infrastructure/supabase/database.types";

export function StakeholderDrawerContent({ id }: { id: string }) {
  const stakeholderQ = useStakeholderDetail(id);
  const update = useUpdateStakeholder();

  if (!stakeholderQ.data) {
    return <p className="text-muted-foreground px-6 py-8 text-xs italic">Loading…</p>;
  }
  const s = stakeholderQ.data;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <header className="border-border/60 flex flex-col gap-1.5 border-b px-6 py-5">
        <span className="eyebrow inline-flex items-center gap-2">
          <span className="font-mono uppercase">{s.org}</span>
          {s.area ? (
            <>
              <span className="text-border">·</span>
              <span className="capitalize">{s.area}</span>
            </>
          ) : null}
        </span>
        <h2 className="text-base font-medium tracking-tight">{s.name}</h2>
        {s.role ? <p className="text-muted-foreground text-xs">{s.role}</p> : null}
      </header>

      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="flex flex-col gap-5">
          <Field label="Name">
            <Input
              defaultValue={s.name}
              onBlur={(e) => {
                const next = e.target.value.trim();
                if (next && next !== s.name) {
                  update.mutate({ id: s.id, patch: { name: next } });
                }
              }}
            />
          </Field>

          <Field label="Role">
            <Input
              defaultValue={s.role ?? ""}
              placeholder="e.g. CRM PM, Engineering Lead"
              onBlur={(e) => {
                const next = e.target.value.trim();
                if (next !== (s.role ?? "")) {
                  update.mutate({ id: s.id, patch: { role: next || null } });
                }
              }}
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Org">
              <Select
                value={s.org}
                onValueChange={(v) => update.mutate({ id: s.id, patch: { org: v as Org } })}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="moveo">Moveo</SelectItem>
                  <SelectItem value="yango">Yango</SelectItem>
                  <SelectItem value="yandex">Yandex</SelectItem>
                  <SelectItem value="3p">3rd party</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Area">
              <Select
                value={s.area ?? "__none__"}
                onValueChange={(v) =>
                  update.mutate({
                    id: s.id,
                    patch: { area: v === "__none__" ? null : (v as Area) },
                  })
                }
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">—</SelectItem>
                  <SelectItem value="technical">Technical</SelectItem>
                  <SelectItem value="business">Business</SelectItem>
                  <SelectItem value="design">Design</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="Contact">
            <Input
              defaultValue={s.contact ?? ""}
              placeholder="email, slack, etc."
              onBlur={(e) => {
                const next = e.target.value.trim();
                if (next !== (s.contact ?? "")) {
                  update.mutate({ id: s.id, patch: { contact: next || null } });
                }
              }}
            />
          </Field>

          <Field label="Availability notes">
            <Textarea
              defaultValue={s.availability_notes ?? ""}
              rows={2}
              placeholder="Time zones, leave, comms preferences."
              onBlur={(e) => {
                const next = e.target.value;
                if (next !== (s.availability_notes ?? "")) {
                  update.mutate({ id: s.id, patch: { availability_notes: next || null } });
                }
              }}
            />
          </Field>

          <p className="text-muted-foreground border-border/40 mt-2 border-t pt-3 font-mono text-[10px] tracking-wider">
            Last updated {format(new Date(s.updated_at), "d MMM, HH:mm")}
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-muted-foreground text-[10px] tracking-wider uppercase">{label}</Label>
      {children}
    </div>
  );
}
